import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Delete, Edit, TrendingUp } from "@mui/icons-material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  INVOICE_STATUS_LABELS,
  useCreateInvoice,
  useCreateTarget,
  useDeleteInvoice,
  useDeleteTarget,
  useFinanceSummary,
  useInvoices,
  useRevenueTargets,
  type Invoice,
  type InvoiceStatus,
  type RevenueTarget,
} from "@/hooks/useFinance";
import { useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";
import { brand } from "@/theme";

const QUARTER_LABELS: Record<number, string> = { 1: "Q1", 2: "Q2", 3: "Q3", 4: "Q4" };

const fmtGBP = (v: number | string): string => {
  const n = typeof v === "string" ? parseFloat(v) : v;
  if (!Number.isFinite(n)) return "£0";
  return `£${n.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
};

interface InvoiceFormState {
  id?: string;
  invoice_number: string;
  invoice_date: string;
  value: string;
  status: InvoiceStatus;
  paid_date: string;
  notes: string;
}

const emptyInvoiceForm: InvoiceFormState = {
  invoice_number: "",
  invoice_date: new Date().toISOString().slice(0, 10),
  value: "",
  status: "issued",
  paid_date: "",
  notes: "",
};

function TargetsCard({ year }: { year: number }) {
  const { data: targets = [] } = useRevenueTargets(year);
  const createMutation = useCreateTarget();
  const deleteMutation = useDeleteTarget();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<{ quarter: number | ""; target_value: string; notes: string }>({
    quarter: "",
    target_value: "",
    notes: "",
  });

  const submit = async () => {
    await createMutation.mutateAsync({
      year,
      quarter: form.quarter === "" ? null : form.quarter,
      target_value: parseFloat(form.target_value),
      notes: form.notes || undefined,
    });
    setOpen(false);
    setForm({ quarter: "", target_value: "", notes: "" });
  };

  const annual = targets.find((t) => t.quarter === null);
  const quarterly = [1, 2, 3, 4].map((q) => targets.find((t) => t.quarter === q));

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>
            Targets · {year}
          </Typography>
          <Button size="small" startIcon={<Add />} onClick={() => setOpen(true)}>
            Set target
          </Button>
        </Box>

        <Stack spacing={1}>
          <TargetRow label="Annual" target={annual} onDelete={(id) => deleteMutation.mutate(id)} />
          {[1, 2, 3, 4].map((q) => (
            <TargetRow
              key={q}
              label={QUARTER_LABELS[q]}
              target={quarterly[q - 1]}
              onDelete={(id) => deleteMutation.mutate(id)}
            />
          ))}
        </Stack>

        <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Set revenue target — {year}</DialogTitle>
          <DialogContent
            sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "16px !important" }}
          >
            <FormControl fullWidth>
              <InputLabel>Period</InputLabel>
              <Select
                value={form.quarter === "" ? "annual" : String(form.quarter)}
                label="Period"
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    quarter:
                      e.target.value === "annual" ? "" : (Number(e.target.value) as 1 | 2 | 3 | 4),
                  }))
                }
              >
                <MenuItem value="annual">Annual</MenuItem>
                {[1, 2, 3, 4].map((q) => (
                  <MenuItem key={q} value={String(q)}>
                    {QUARTER_LABELS[q]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Target (£)"
              type="number"
              value={form.target_value}
              onChange={(e) => setForm((f) => ({ ...f, target_value: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Notes (optional)"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={submit} disabled={!form.target_value}>
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
}

function TargetRow({
  label,
  target,
  onDelete,
}: {
  label: string;
  target: RevenueTarget | undefined;
  onDelete: (id: string) => void;
}) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography variant="body2" sx={{ width: 70, color: "text.secondary" }}>
        {label}
      </Typography>
      <Typography variant="body1" fontWeight={600} sx={{ flex: 1 }}>
        {target ? fmtGBP(target.target_value) : "—"}
      </Typography>
      {target && (
        <IconButton size="small" title="Delete" onClick={() => onDelete(target.id)}>
          <Delete fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
}

function SummaryHeader({ year }: { year: number }) {
  const { data: summary } = useFinanceSummary(year);
  if (!summary) return null;
  const annualIssued = parseFloat(summary.annual_issued);
  const annualPaid = parseFloat(summary.annual_paid);
  const annualTarget = parseFloat(summary.annual_target);
  const pctIssued = annualTarget > 0 ? (annualIssued / annualTarget) * 100 : 0;
  const pctPaid = annualTarget > 0 ? (annualPaid / annualTarget) * 100 : 0;

  const cards = [
    { label: "Annual target", value: fmtGBP(annualTarget), color: "text.primary" },
    {
      label: "Issued YTD",
      value: fmtGBP(annualIssued),
      caption: annualTarget > 0 ? `${pctIssued.toFixed(1)}% of target` : undefined,
      color: brand.forestGreen,
    },
    {
      label: "Paid YTD",
      value: fmtGBP(annualPaid),
      caption: annualTarget > 0 ? `${pctPaid.toFixed(1)}% of target` : undefined,
      color: brand.warmGold,
    },
  ];

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2 }}>
      {cards.map((c) => (
        <Paper key={c.label} sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {c.label}
          </Typography>
          <Typography variant="h4" sx={{ color: c.color, fontWeight: 700 }}>
            {c.value}
          </Typography>
          {c.caption && (
            <Typography variant="caption" color="text.secondary">
              {c.caption}
            </Typography>
          )}
        </Paper>
      ))}
    </Box>
  );
}

function QuarterChart({ year }: { year: number }) {
  const { data: summary } = useFinanceSummary(year);
  const chartData = useMemo(() => {
    if (!summary) return [];
    return summary.quarters.map((q) => ({
      label: QUARTER_LABELS[q.quarter],
      target: parseFloat(q.target),
      issued: parseFloat(q.issued_value),
      paid: parseFloat(q.paid_value),
    }));
  }, [summary]);

  if (!summary) return null;

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Quarterly target vs actual · {year}
        </Typography>
        <Box sx={{ height: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" />
              <YAxis tickFormatter={(v: number) => fmtGBP(v)} width={80} />
              <Tooltip formatter={(v: number) => fmtGBP(v)} />
              <Legend />
              <Bar dataKey="target" fill="#cbd5e1" name="Target" />
              <Bar dataKey="issued" fill={brand.forestGreen} name="Issued" />
              <Bar dataKey="paid" fill={brand.warmGold} name="Paid" />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
}

function InvoicesTable({ year }: { year: number }) {
  const { data: invoices = [], isLoading } = useInvoices({ year });
  const createMutation = useCreateInvoice();
  const deleteMutation = useDeleteInvoice();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<InvoiceFormState>(emptyInvoiceForm);

  const submit = async () => {
    const payload: Record<string, unknown> = {
      invoice_number: form.invoice_number,
      invoice_date: form.invoice_date,
      value: parseFloat(form.value),
      status: form.status,
      paid_date: form.paid_date || null,
      notes: form.notes || null,
    };
    if (editingId) {
      await apiClient.put(`/finance/invoices/${editingId}`, payload);
      await queryClient.invalidateQueries({ queryKey: ["finance-invoices"] });
      await queryClient.invalidateQueries({ queryKey: ["finance-summary"] });
    } else {
      await createMutation.mutateAsync(payload);
    }
    setOpen(false);
    setEditingId(null);
    setForm(emptyInvoiceForm);
  };

  const startEdit = (inv: Invoice) => {
    setEditingId(inv.id);
    setForm({
      invoice_number: inv.invoice_number,
      invoice_date: inv.invoice_date,
      value: inv.value,
      status: inv.status,
      paid_date: inv.paid_date ?? "",
      notes: inv.notes ?? "",
    });
    setOpen(true);
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>
            Invoices · {year} ({invoices.length})
          </Typography>
          <Button
            size="small"
            startIcon={<Add />}
            onClick={() => {
              setEditingId(null);
              setForm(emptyInvoiceForm);
              setOpen(true);
            }}
          >
            Add invoice
          </Button>
        </Box>

        {isLoading ? (
          <CircularProgress size={24} />
        ) : invoices.length === 0 ? (
          <Alert severity="info">
            No invoices for {year} yet. Add one to start tracking actual vs target.
          </Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Invoice #</TableCell>
                <TableCell>Issued</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Paid</TableCell>
                <TableCell align="right">Value</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.map((inv) => (
                <TableRow key={inv.id} hover>
                  <TableCell sx={{ fontWeight: 500 }}>{inv.invoice_number}</TableCell>
                  <TableCell>{inv.invoice_date}</TableCell>
                  <TableCell>
                    <Chip
                      label={INVOICE_STATUS_LABELS[inv.status]}
                      size="small"
                      color={
                        inv.status === "paid"
                          ? "success"
                          : inv.status === "cancelled"
                            ? "default"
                            : "primary"
                      }
                    />
                  </TableCell>
                  <TableCell>{inv.paid_date ?? "—"}</TableCell>
                  <TableCell align="right">{fmtGBP(inv.value)}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" title="Edit" onClick={() => startEdit(inv)}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      title="Delete"
                      onClick={() => deleteMutation.mutate(inv.id)}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <Dialog
          open={open}
          onClose={() => {
            setOpen(false);
            setEditingId(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{editingId ? "Edit invoice" : "Add invoice"}</DialogTitle>
          <DialogContent
            sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "16px !important" }}
          >
            <TextField
              label="Invoice number"
              value={form.invoice_number}
              onChange={(e) => setForm((f) => ({ ...f, invoice_number: e.target.value }))}
              fullWidth
              required
            />
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Issue date"
                type="date"
                value={form.invoice_date}
                onChange={(e) => setForm((f) => ({ ...f, invoice_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Value (£)"
                type="number"
                value={form.value}
                onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))}
                fullWidth
                required
              />
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={form.status}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, status: e.target.value as InvoiceStatus }))
                  }
                >
                  <MenuItem value="issued">Issued</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Paid date"
                type="date"
                value={form.paid_date}
                onChange={(e) => setForm((f) => ({ ...f, paid_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
                helperText={form.status === "paid" ? "Required when paid" : undefined}
              />
            </Box>
            <TextField
              label="Notes (optional)"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={submit}
              disabled={!form.invoice_number || !form.value}
            >
              {editingId ? "Save" : "Add"}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
}

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [2025, 2026, 2027, 2028].filter((y) => y <= CURRENT_YEAR + 1);

export default function Finance() {
  const [year, setYear] = useState(CURRENT_YEAR < 2025 ? 2025 : CURRENT_YEAR);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <TrendingUp color="action" />
        <Typography variant="h4" sx={{ flex: 1 }}>
          Finance
        </Typography>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Year</InputLabel>
          <Select label="Year" value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {YEAR_OPTIONS.map((y) => (
              <MenuItem key={y} value={y}>
                {y}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Set annual + quarterly revenue targets, log invoices as they're issued and paid, and track
        target-vs-actual rollups. Manual entry for now; the FreeAgent sync will pull invoices in
        automatically once it lands.{" "}
        <em>Tip: log historical invoices first so the year-to-date chart fills in.</em>
      </Typography>

      <Stack spacing={2}>
        <SummaryHeader year={year} />
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 2fr" }, gap: 2 }}>
          <TargetsCard year={year} />
          <QuarterChart year={year} />
        </Box>
        <InvoicesTable year={year} />
      </Stack>
    </Box>
  );
}
